import { CircularProgress } from "@material-ui/core";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from "../../airlyze_logo.png";
import {
  formatTimeWithTZOffset,
  getIntegerFromSpelledNumber,
  getTimestampWithoutOffset,
  getToken,
  numberToWord,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";

const server = EnvSettings.server;

const sanitize_block = (block) => {
  if (typeof block === "string" || typeof block === "number") {
    return <Text>{block}</Text>;
  } else {
    return block;
  }
};

const getDeviceType = (data) => {
  switch (data) {
    case 1:
      return "Medibag";
    case 2:
      return "Sensor Module";
    default:
      break;
  }
};

const tstyles = StyleSheet.create({
  table: {
    fontSize: 10,
    display: "table",
    width: "auto",
    marginTop: 5,
    marginHorizontal: "auto",
  },
  row: {
    flexDirection: "row",
  },
  cell: {
    padding: "5px",
    width: 50,
    textAlign: "center",
  },
});

const Table = ({
  data,
  style_function = (row_index, col_index, data) => {
    return StyleSheet.create({ fontWeight: "bold" });
  },
  style = {},
}) => {
  return (
    <View style={[tstyles.table, style]}>
      {data.map((row, row_index) => (
        <View
          key={row_index}
          style={{
            ...tstyles.row,
            backgroundColor: row_index % 2 === 0 ? "white" : "#c9c9c9",
          }}
          wrap={false}
        >
          {row.map((cell, col_index) => (
            <View
              key={col_index}
              style={[
                tstyles.cell,
                style_function(row_index, col_index, data),
                { width: [`${100 / row.length}%`] },
              ]}
            >
              {sanitize_block(cell)}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};

const findStartingTime = (data) => {
  var st = undefined;
  for (let index = 0; index < data.length; index++) {
    const element = data[index];
    if (element.Measurements && element.Measurements.length > 0) {
      st = element.Measurements[0].timeStamp;
      break;
    }
  }
  return st;
};

const findEndingTime = (data) => {
  var st = undefined;
  for (let index = 0; index < data.length; index++) {
    const element = data[index];
    if (element.Measurements && element.Measurements.length > 0) {
      st = element.Measurements[element.Measurements.length - 1].timeStamp;
      break;
    }
  }
  return st;
};

function ReportDoc(props) {
  const { t } = useTranslation();
  var data = props.data;

  function formatTempForTable(name, value, devconfig) {
    var d = "sensor" + getIntegerFromSpelledNumber(name.replace("temp", ""));
    if (
      devconfig[d] &&
      (value >= devconfig[d].alertHigh || value <= devconfig[d].alertLow)
    ) {
      return `${value} *`;
    }
    return value;
  }

  function formatTableData(data, devconfig) {
    var arr = [];
    var arr2 = [];
    var cols = ["timeStamp"];
    var currdate = "";
    arr2.push(t("report.timeStamp"));
    Object.keys(devconfig).forEach((key, index) => {
      if (key.includes("sensor")) {
        if (devconfig[key] !== null) {
          if (devconfig[key].name) {
            arr2.push(devconfig[key].name + " (°C)");
          } else {
            arr2.push(`T${key.replace("sensor", "")} (°C)`);
          }
          cols.push("temp" + numberToWord(cols.length));
        }
      }
    });
    arr.push(arr2);
    data.forEach((element) => {
      arr2 = [];
      cols.forEach((col) => {
        if (col === "timeStamp") {
          var date = formatTimeWithTZOffset(element[col]);
          if (currdate !== date.substr(0, 10) && currdate !== "") {
            arr.push([date.substr(0, 10), "", "", ""]);
          }
          currdate = date.substr(0, 10);
          arr2.push(date.substr(11, 19));
        } else if (col.includes("temp")) {
          if (element[col]) {
            arr2.push(formatTempForTable(col, element[col] / 100, devconfig));
          } else {
            arr2.push("");
          }
        } else {
          arr2.push(element[col]);
        }
      });
      arr.push(arr2);
    });
    return arr;
  }

  return (
    <Document>
      <Page style={styles.body}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <Text style={styles.title}>
            {data.params?.transport_id
              ? t("report.transportHeader")
              : t("report.contHeader")}
          </Text>
          <Image style={styles.logoimg} src={logo}></Image>
        </View>
        <View style={styles.horizontalLine} />
        {Array.isArray(data) ? (
          <View>
            <View style={styles.descriptionField}>
              <Text style={styles.description}>{`${t("report.company")} ${
                data[0].Company.companyName
              }`}</Text>
              <Text style={styles.description}>{`${t(
                "report.durationFrom"
              )} ${formatTimeWithTZOffset(findStartingTime(data))}`}</Text>
              <Text style={styles.description}>{`${t(
                "report.durationTo"
              )} ${formatTimeWithTZOffset(findEndingTime(data))}`}</Text>
            </View>
            <View style={styles.horizontalLine} />
            {data.map((row) => (
              <View>
                <View style={styles.descriptionField}>
                  <Text style={styles.description}>
                    {`${t("report.device")} ${getDeviceType(row.deviceType)}, ${
                      row.serialNumber
                    }` + (row.deviceName !== null ? ", " + row.deviceName : "")}
                  </Text>
                </View>
                <View style={styles.horizontalLine} />
                <Table
                  data={formatTableData(row.Measurements, row.DeviceConfig)}
                ></Table>
                <View style={styles.horizontalLine} />
              </View>
            ))}
          </View>
        ) : (
          <View>
            <View style={styles.descriptionField}>
              <Text style={styles.description}>{`${t("report.company")} ${
                data.Device.Company?.companyName || ""
              }`}</Text>
              <Text style={styles.description}>
                {`${t("report.device")} ${getDeviceType(
                  data.Device.deviceType
                )}, ${data.Device.serialNumber}` +
                  (data.Device.deviceName ? ", " + data.Device.deviceName : "")}
              </Text>
              <Text style={styles.description}>{`${t(
                "report.durationFrom"
              )} ${formatTimeWithTZOffset(data.startTime)}`}</Text>
              <Text style={styles.description}>{`${t(
                "report.durationTo"
              )} ${formatTimeWithTZOffset(data.endTime)}`}</Text>
            </View>
            <View style={styles.horizontalLine} />
            <Table
              data={formatTableData(
                data.Measurements,
                data.Device?.DeviceConfig
              )}
            ></Table>
          </View>
        )}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}

const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  title: {
    fontSize: 24,
  },
  logoimg: {
    width: 63,
    height: 50,
  },
  horizontalLine: {
    borderBottomColor: "black",
    borderBottomWidth: 1,
    paddingTop: 5,
    paddingBottom: 5,
  },
  descriptionField: {
    marginTop: 5,
    fontSize: 10,
  },
  description: {
    padding: 5,
  },
  author: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 40,
  },
  subtitle: {
    fontSize: 18,
    margin: 12,
  },
  text: {
    margin: 12,
    fontSize: 14,
    textAlign: "justify",
    fontFamily: "Times-Roman",
  },
  image: {
    marginVertical: 15,
    marginHorizontal: 100,
  },
  header: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: "center",
    color: "grey",
  },
  pageNumber: {
    position: "absolute",
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },
});

export default function Report(props) {
  const [reportData, setReportData] = useState();
  //classes.root.overflow = "hidden";

  let getReportData = () => {
    var requestOptions;
    var endPoint;
    if (props.computedMatch.params.transport_id) {
      requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          transportid: props.computedMatch.params.transport_id,
        }),
      };
      endPoint = "/get_transportation_data";
    } else {
      var sdate =
        props.computedMatch.params.date_start.length < 19
          ? props.computedMatch.params.date_start + " 00:00:00"
          : props.computedMatch.params.date_start;
      var edate =
        props.computedMatch.params.date_end.length < 19
          ? props.computedMatch.params.date_end + " 23:59:59"
          : props.computedMatch.params.date_end;
      requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          time_period_start: getTimestampWithoutOffset(sdate),
          time_period_end: getTimestampWithoutOffset(edate),
          deviceid: props.computedMatch.params.device_id,
        }),
      };
      endPoint = "/get_continuous_data";
    }
    fetch(server + endPoint, requestOptions)
      .then((res) => res.json())
      .then((result) => {
        if (process.env.NODE_ENV !== "production") {
          console.log(result);
        }
        if (result.error) {
          //Error
        } else {
          if (props.computedMatch.params.transport_id) {
            setReportData(result.transport);
          } else {
            setReportData(result.devices);
          }
        }
      });
  };

  useEffect(() => {
    if (!reportData) {
      getReportData();
    }
  });

  return reportData ? (
    <PDFViewer
      width="100%"
      style={{ height: "100vh", border: 0, margin: 0, padding: 0 }}
    >
      <ReportDoc data={reportData} params={props.computedMatch.params} />
    </PDFViewer>
  ) : (
    <CircularProgress></CircularProgress>
  );
}
